import React, { useState, useEffect, useCallback } from 'react';
import RoleBasedHeader from '../../components/ui/RoleBasedHeader';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import {
  fetchScraperOverview,
  submitScraperOtp,
  requestManualScraperRun,
  fetchScraperLoginNumberMasked,
  updateScraperLoginNumber,
} from './data';

const MOBILE_NUMBER_PATTERN = /^[6-9][0-9]{9}$/;

// Status -> badge color. Uses the same semantic tokens (success/warning/
// error/muted) as the rest of the admin UI rather than inventing new colors
// -- see tailwind.config.js. OTP_REQUIRED/LOGIN_REQUIRED get the warning
// (amber) treatment since they need admin attention but are not failures;
// FAILED gets error; SUCCESS/IDLE get success; everything mid-run (RUNNING/
// SCRAPING/RESUMING/OTP_SUBMITTED) gets the brand blue used elsewhere for
// "in progress".
const STATUS_STYLES = {
  IDLE: { className: 'bg-success/10 text-success', label: 'Idle' },
  SUCCESS: { className: 'bg-success/10 text-success', label: 'Success' },
  RUNNING: { className: 'bg-[#1e4a72]/10 text-[#1e4a72]', label: 'Running' },
  SCRAPING: { className: 'bg-[#1e4a72]/10 text-[#1e4a72]', label: 'Scraping' },
  RESUMING: { className: 'bg-[#1e4a72]/10 text-[#1e4a72]', label: 'Resuming' },
  OTP_SUBMITTED: { className: 'bg-[#1e4a72]/10 text-[#1e4a72]', label: 'OTP Submitted' },
  LOGIN_REQUIRED: { className: 'bg-warning/10 text-warning', label: 'Login Required' },
  OTP_REQUIRED: { className: 'bg-warning/10 text-warning', label: 'OTP Required' },
  FAILED: { className: 'bg-error/10 text-error', label: 'Failed' },
  DISABLED: { className: 'bg-muted text-muted-foreground', label: 'Disabled' },
};

const RUN_STATUS_STYLES = {
  RUNNING: { className: 'bg-[#1e4a72]/10 text-[#1e4a72]', label: 'Running' },
  SUCCESS: { className: 'bg-success/10 text-success', label: 'Success' },
  NO_NEW_DATA: { className: 'bg-success/10 text-success', label: 'No New Data' },
  FAILED: { className: 'bg-error/10 text-error', label: 'Failed' },
  CANCELLED: { className: 'bg-muted text-muted-foreground', label: 'Cancelled' },
};

const StatusBadge = ({ value, styleMap }) => {
  const style = styleMap?.[value] || { className: 'bg-muted text-muted-foreground', label: value || 'Unknown' };
  return (
    <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${style.className}`}>
      {style.label}
    </span>
  );
};

const formatDateTime = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' });
  } catch {
    return iso;
  }
};

const formatDuration = (startedAt, finishedAt) => {
  if (!startedAt || !finishedAt) return '—';
  const ms = new Date(finishedAt).getTime() - new Date(startedAt).getTime();
  if (!Number.isFinite(ms) || ms < 0) return '—';
  const seconds = Math.round(ms / 1000);
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  const remSeconds = seconds % 60;
  return `${minutes}m ${remSeconds}s`;
};

const Stat = ({ label, value }) => (
  <div>
    <div className="text-xs text-muted-foreground">{label}</div>
    <div className="text-sm font-semibold text-foreground">{value ?? '—'}</div>
  </div>
);

// `collapsible` is opt-in and defaults to false, so every existing Card
// usage (Current Status, Latest Run, OTP Panel, Login Number, Manual
// Control) renders identically to before -- only Recent Run History passes
// collapsible/expanded/onToggleExpand.
const Card = ({ title, icon, children, collapsible = false, expanded = true, onToggleExpand }) => (
  <div className="bg-card rounded-lg border border-border shadow-sm p-4 sm:p-5 md:p-6">
    {collapsible ? (
      <button type="button" onClick={onToggleExpand} className="flex items-center gap-2 mb-4 w-full text-left">
        {icon && <Icon name={icon} size={18} className="text-[#1e4a72]" />}
        <h2 className="text-base sm:text-lg font-semibold text-foreground flex-1">{title}</h2>
        <Icon name={expanded ? 'ChevronUp' : 'ChevronDown'} size={18} className="text-muted-foreground" />
      </button>
    ) : (
      <div className="flex items-center gap-2 mb-4">
        {icon && <Icon name={icon} size={18} className="text-[#1e4a72]" />}
        <h2 className="text-base sm:text-lg font-semibold text-foreground">{title}</h2>
      </div>
    )}
    {(!collapsible || expanded) && children}
  </div>
);

const ScraperControl = () => {
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [otpValue, setOtpValue] = useState('');
  const [otpSubmitting, setOtpSubmitting] = useState(false);
  const [otpError, setOtpError] = useState(null);

  const [manualRequesting, setManualRequesting] = useState(false);
  const [manualError, setManualError] = useState(null);
  const [manualSuccessMessage, setManualSuccessMessage] = useState(null);

  const [loginNumberMasked, setLoginNumberMasked] = useState(null);
  const [loginNumberInput, setLoginNumberInput] = useState('');
  const [loginNumberSubmitting, setLoginNumberSubmitting] = useState(false);
  const [loginNumberError, setLoginNumberError] = useState(null);
  const [loginNumberSuccess, setLoginNumberSuccess] = useState(false);

  const [runHistoryExpanded, setRunHistoryExpanded] = useState(true);

  const load = useCallback(async () => {
    const { overview: data, error: err } = await fetchScraperOverview('ebuysugar');
    setOverview(data);
    setError(err);
    setLoading(false);

    if (data?.config?.id) {
      const { masked, error: maskedError } = await fetchScraperLoginNumberMasked(data.config.id);
      setLoginNumberMasked(masked);
      if (maskedError) setLoginNumberError(maskedError);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleSubmitOtp = async (e) => {
    e.preventDefault();
    if (!overview?.activeOtpRequest?.id || !otpValue.trim()) return;
    setOtpSubmitting(true);
    setOtpError(null);
    const { success, error: err } = await submitScraperOtp(overview.activeOtpRequest.id, otpValue.trim());
    setOtpSubmitting(false);
    // Clear the field regardless of outcome -- the value must never linger
    // in the DOM, in component state, or be re-displayed after submission.
    setOtpValue('');
    if (!success) {
      setOtpError(err);
      return;
    }
    await load();
  };

  const handleUpdateLoginNumber = async (e) => {
    e.preventDefault();
    const trimmed = loginNumberInput.trim();
    if (!overview?.config?.id) return;
    setLoginNumberSuccess(false);
    if (!MOBILE_NUMBER_PATTERN.test(trimmed)) {
      setLoginNumberError('Enter a valid 10-digit Indian mobile number (starts with 6-9).');
      return;
    }
    setLoginNumberSubmitting(true);
    setLoginNumberError(null);
    const { success, error: err } = await updateScraperLoginNumber(overview.config.id, trimmed);
    setLoginNumberSubmitting(false);
    // Clear the field regardless of outcome -- the typed number must never
    // linger in the DOM/component state once submitted, same as the OTP field.
    setLoginNumberInput('');
    if (!success) {
      setLoginNumberError(err);
      return;
    }
    setLoginNumberSuccess(true);
    const { masked } = await fetchScraperLoginNumberMasked(overview.config.id);
    setLoginNumberMasked(masked);
  };

  const handleRequestManualRun = async () => {
    if (!overview?.config?.id) return;
    setManualRequesting(true);
    setManualError(null);
    setManualSuccessMessage(null);
    const { error: err, dispatched } = await requestManualScraperRun(overview.config.id);
    setManualRequesting(false);
    if (err) {
      setManualError(err);
      return;
    }
    setManualSuccessMessage(
      dispatched ? 'Manual run requested. Cloud worker started.' : 'Manual run requested.'
    );
    await load();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <RoleBasedHeader />
        <main className="main-content">
          <div className="max-w-[1200px] mx-auto px-4 py-8 text-sm text-muted-foreground">Loading scraper status…</div>
        </main>
      </div>
    );
  }

  if (!overview) {
    return (
      <div className="min-h-screen bg-background">
        <RoleBasedHeader />
        <main className="main-content">
          <div className="max-w-[1200px] mx-auto px-4 py-8">
            <p className="text-sm text-error">
              {error || 'No scraper configuration found for "ebuysugar".'}
            </p>
          </div>
        </main>
      </div>
    );
  }

  const { config, status, latestRun, recentRuns, activeOtpRequest, activeManualRequest } = overview;
  const displayedStatus = !config.enabled ? 'DISABLED' : status?.status || 'IDLE';
  const otpExpired = activeOtpRequest?.status === 'PENDING' && new Date(activeOtpRequest.expires_at) < new Date();

  return (
    <div className="min-h-screen bg-background">
      <RoleBasedHeader />
      <main className="main-content">
        <div className="max-w-[1200px] mx-auto px-3 sm:px-4 md:px-6 lg:px-8 py-4 sm:py-6 lg:py-8 space-y-4 sm:space-y-6">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-lg bg-gradient-to-br from-[#1e4a72] to-[#ff8c00] flex items-center justify-center flex-shrink-0">
              <Icon name="Bot" size={22} color="#FFFFFF" />
            </div>
            <div className="min-w-0">
              <h1 className="text-lg sm:text-xl md:text-2xl font-bold text-foreground truncate">Scraper Control</h1>
              <p className="text-xs sm:text-sm text-muted-foreground truncate">
                Monitoring and control for the eBuySugar data pipeline
              </p>
            </div>
          </div>

          {error && (
            <div className="bg-error/10 text-error text-sm rounded-lg p-3 border border-error/20">{error}</div>
          )}

          {/* SECTION 1 — Current status */}
          <Card title="Current Status" icon="Activity">
            <div className="flex flex-wrap items-center gap-3 mb-4">
              <span className="text-sm text-muted-foreground">Source:</span>
              <span className="text-sm font-medium text-foreground">{config.display_name}</span>
              <StatusBadge value={displayedStatus} styleMap={STATUS_STYLES} />
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <Stat label="Last Heartbeat" value={formatDateTime(status?.last_heartbeat_at)} />
              <Stat label="Last Run Started" value={formatDateTime(status?.last_started_at)} />
              <Stat label="Last Successful Run" value={formatDateTime(status?.last_success_at)} />
              <Stat
                label="Last Error"
                value={status?.last_error_message ? `${formatDateTime(status.last_error_at)} — ${status.last_error_message}` : '—'}
              />
            </div>
          </Card>

          {/* SECTION 2 — Latest run */}
          <Card title="Current Run / Latest Result" icon="History">
            {!latestRun ? (
              <p className="text-sm text-muted-foreground">No scraper runs have been recorded yet.</p>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                <Stat label="Started" value={formatDateTime(latestRun.started_at)} />
                <Stat label="Finished" value={formatDateTime(latestRun.finished_at)} />
                <Stat label="Duration" value={formatDuration(latestRun.started_at, latestRun.finished_at)} />
                <div>
                  <div className="text-xs text-muted-foreground">Status</div>
                  <StatusBadge value={latestRun.status} styleMap={RUN_STATUS_STYLES} />
                </div>
                <Stat label="Trigger Type" value={latestRun.trigger_type} />
                <Stat label="Pages Checked" value={latestRun.pages_checked} />
                <Stat label="Records Seen" value={latestRun.records_seen} />
                <Stat label="New Records" value={latestRun.records_new} />
                <Stat label="Known Records" value={latestRun.records_known} />
                <Stat label="Promoted Records" value={latestRun.records_promoted} />
                <Stat label="Failed Records" value={latestRun.records_failed} />
                {latestRun.error_message && (
                  <div className="col-span-2 sm:col-span-4">
                    <div className="text-xs text-muted-foreground">Error</div>
                    <div className="text-sm text-error">{latestRun.error_message}</div>
                  </div>
                )}
              </div>
            )}
          </Card>

          {/* SECTION 3 — OTP action panel */}
          <Card title="OTP Action Panel" icon="ShieldCheck">
            {!activeOtpRequest ? (
              <p className="text-sm text-muted-foreground">No OTP action required.</p>
            ) : activeOtpRequest.status === 'OTP_SUBMITTED' ? (
              <div className="flex items-center gap-2 text-sm text-[#1e4a72]">
                <Icon name="Loader" size={16} className="animate-spin" />
                OTP Submitted. Waiting for scraper to resume…
              </div>
            ) : otpExpired ? (
              <p className="text-sm text-error">
                This OTP request expired at {formatDateTime(activeOtpRequest.expires_at)}. Waiting for the scraper to request a new one.
              </p>
            ) : (
              <div>
                <p className="text-sm text-warning font-medium mb-1">OTP Required</p>
                <p className="text-xs text-muted-foreground mb-4">
                  Requested at {formatDateTime(activeOtpRequest.requested_at)} — expires {formatDateTime(activeOtpRequest.expires_at)}
                </p>
                <form onSubmit={handleSubmitOtp} className="flex flex-col sm:flex-row gap-3 sm:items-end max-w-sm">
                  <div className="flex-1">
                    <Input
                      type="text"
                      inputMode="numeric"
                      autoComplete="one-time-code"
                      label="Enter OTP"
                      placeholder="6-digit code"
                      value={otpValue}
                      onChange={(e) => setOtpValue(e.target.value)}
                      maxLength={10}
                    />
                  </div>
                  <Button type="submit" loading={otpSubmitting} disabled={!otpValue.trim()}>
                    Submit OTP
                  </Button>
                </form>
                {otpError && <p className="text-sm text-error mt-2">{otpError}</p>}
              </div>
            )}
          </Card>

          {/* SECTION 3b — eBuySugar login number */}
          <Card title="eBuySugar Login Number" icon="Phone">
            <p className="text-xs text-muted-foreground mb-4">
              The mobile number the worker submits at login. Changing it takes effect on the worker's next run —
              no code change or redeploy needed. The full number is never shown here, only the current masked value.
            </p>
            <div className="flex flex-wrap items-center gap-3 mb-4">
              <span className="text-sm text-muted-foreground">Current number:</span>
              <span className="text-sm font-medium text-foreground font-mono">
                {loginNumberMasked || 'Not configured'}
              </span>
            </div>
            <form onSubmit={handleUpdateLoginNumber} className="flex flex-col sm:flex-row gap-3 sm:items-end max-w-sm">
              <div className="flex-1">
                <Input
                  type="text"
                  inputMode="numeric"
                  autoComplete="off"
                  label={loginNumberMasked ? 'Change number' : 'Set number'}
                  placeholder="10-digit mobile number"
                  value={loginNumberInput}
                  onChange={(e) => {
                    setLoginNumberInput(e.target.value);
                    setLoginNumberSuccess(false);
                  }}
                  maxLength={10}
                />
              </div>
              <Button type="submit" loading={loginNumberSubmitting} disabled={!loginNumberInput.trim()}>
                Save
              </Button>
            </form>
            {loginNumberError && <p className="text-sm text-error mt-2">{loginNumberError}</p>}
            {loginNumberSuccess && <p className="text-sm text-success mt-2">Login number updated.</p>}
          </Card>

          {/* SECTION 4 — Recent run history */}
          <Card
            title="Recent Run History"
            icon="ListOrdered"
            collapsible
            expanded={runHistoryExpanded}
            onToggleExpand={() => setRunHistoryExpanded((v) => !v)}
          >
            {recentRuns.length === 0 ? (
              <p className="text-sm text-muted-foreground">No runs to show yet.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs text-muted-foreground border-b border-border">
                      <th className="py-2 pr-3 font-medium">Started</th>
                      <th className="py-2 pr-3 font-medium">Finished</th>
                      <th className="py-2 pr-3 font-medium">Trigger</th>
                      <th className="py-2 pr-3 font-medium">Status</th>
                      <th className="py-2 pr-3 font-medium">Pages</th>
                      <th className="py-2 pr-3 font-medium">Seen</th>
                      <th className="py-2 pr-3 font-medium">New</th>
                      <th className="py-2 pr-3 font-medium">Promoted</th>
                      <th className="py-2 pr-3 font-medium">Error</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recentRuns.map((run) => (
                      <tr key={run.id} className="border-b border-border last:border-0">
                        <td className="py-2 pr-3 whitespace-nowrap">{formatDateTime(run.started_at)}</td>
                        <td className="py-2 pr-3 whitespace-nowrap">{formatDateTime(run.finished_at)}</td>
                        <td className="py-2 pr-3">{run.trigger_type}</td>
                        <td className="py-2 pr-3">
                          <StatusBadge value={run.status} styleMap={RUN_STATUS_STYLES} />
                        </td>
                        <td className="py-2 pr-3">{run.pages_checked ?? '—'}</td>
                        <td className="py-2 pr-3">{run.records_seen ?? '—'}</td>
                        <td className="py-2 pr-3">{run.records_new ?? '—'}</td>
                        <td className="py-2 pr-3">{run.records_promoted ?? '—'}</td>
                        <td className="py-2 pr-3 text-error max-w-[220px] truncate" title={run.error_message || ''}>
                          {run.error_message || '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>

          {/* SECTION 5 — Manual control */}
          <Card title="Manual Control" icon="Hand">
            <p className="text-xs text-muted-foreground mb-4">
              Manual mode is backup/standby only. Normal operation will run automatically (Monday–Saturday,
              every 15 minutes) once the cloud worker for this scraper is scheduled in a later phase.
            </p>
            {activeManualRequest ? (
              <p className="text-sm text-[#1e4a72]">
                Manual run request queued for scraper worker (status: {activeManualRequest.status}, requested{' '}
                {formatDateTime(activeManualRequest.requested_at)}).
              </p>
            ) : (
              <>
                <Button
                  variant="outline"
                  iconName="Play"
                  loading={manualRequesting}
                  disabled={!config.enabled}
                  onClick={handleRequestManualRun}
                >
                  Request Manual Run
                </Button>
                {!config.enabled && (
                  <p className="text-xs text-muted-foreground mt-2">This scraper is disabled.</p>
                )}
              </>
            )}
            {manualSuccessMessage && <p className="text-sm text-success mt-2">{manualSuccessMessage}</p>}
            {manualError && <p className="text-sm text-error mt-2">{manualError}</p>}
            <p className="text-xs text-muted-foreground mt-4">
              Requesting a manual run starts the cloud worker (GitHub Actions) immediately, which then claims
              this request and runs one complete scrape cycle.
            </p>
          </Card>
        </div>
      </main>
    </div>
  );
};

export default ScraperControl;
